import React, { FC, useContext, useState } from 'react';
import { useGetRockerboxDocuments } from '../hooks/useGetRockerboxDocuments';
import { Box, Button, IconButton, InputBase, Paper, Stack, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, CircularProgress } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import ShareDocumentModal from './ShareDocumentModal';
import dayjs from 'dayjs';
import { authContext } from '@staffedupusa/shared-components';

type RockerboxDocumentsProps = {
    companyId: number;
    statusValue: string;
};

const RockerboxDocuments: FC<RockerboxDocumentsProps> = ({ companyId, statusValue }) => {
    const { authData } = useContext(authContext);
    const [searchValue, setSearchValue] = useState('');
    const [errorText, setErrorText] = useState('');
    const [showShareDocumentModal, setShowShareDocumentModal] = useState(false);
    const { documentsList, isLoadingDocumentsList } = useGetRockerboxDocuments({ statusValue, searchValue, companyId, setErrorText });

    return (
        <>
            <Stack
                direction={{ xs: 'column', sm: 'row' }}
                justifyContent="space-between"
                alignItems="center"
                spacing={2}
                sx={{ mb: 2 }}
            >
                <Paper component="form" sx={{ pl: '10px', display: 'flex', alignItems: 'center', maxWidth: 400, width: '100%', borderRadius: 5 }}>
                    <InputBase
                        sx={{ ml: 1, flex: 1 }}
                        placeholder="Document Search"
                        inputProps={{ 'aria-label': 'search' }}
                        value={searchValue}
                        onChange={(e) => setSearchValue(e.target.value)}
                    />
                    <IconButton type="button" sx={{ px: '10px' }} aria-label="search">
                        <SearchIcon />
                    </IconButton>
                </Paper>
                <Button
                    onClick={() => setShowShareDocumentModal(true)}
                    startIcon={<AddIcon />}
                    variant="contained"
                    sx={{ mt: 2, width: { sm: '100%', lg: "200px" } }}
                >
                    Share Document
                </Button>
            </Stack>
            <Stack
                direction={{ xs: 'column', sm: 'row' }}
                justifyContent="space-between"
                spacing={5}
            >
                <Stack sx={{ width: "100%" }} spacing={2}>
                    <Typography variant='h3'>Shared Documents</Typography>
                    <TableContainer component={Paper}>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Document Name</TableCell>
                                    <TableCell>Date Updated</TableCell>
                                    <TableCell>Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {documentsList && documentsList.length > 0 ? documentsList.filter((item: { uploaded_by: number; }) => item.uploaded_by === authData?.user?.id).map((document: { date_updated: string; id: React.Key; name: string; url: string | URL; }) => (
                                    <TableRow key={document.id}>
                                        <TableCell>{document.name}</TableCell>
                                        <TableCell>{dayjs(document.date_updated).format('MM/DD/YY')}</TableCell>
                                        <TableCell>
                                            <Button onClick={() => {
                                                window.open(document.url, '_blank');
                                            }} size="small" variant="contained" color="primary">
                                                View
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                )) : isLoadingDocumentsList ? (
                                    <TableRow>
                                        <TableCell colSpan={3} align="center">
                                            <Box sx={{
                                                display: 'flex', alignItems: "center", justifyContent: "center",
                                                textAlign: "center", alignSelf: "center",
                                                width: 'auto', height: '100px', margin: "auto"
                                            }}>

                                                <CircularProgress />
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    <TableRow>
                                        <TableCell colSpan={3} align="center">No Shared Documents</TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Stack>

                <Stack sx={{ width: "100%" }} spacing={2}>
                    <Typography variant='h3'>Received Documents</Typography>
                    <TableContainer component={Paper}>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Document Name</TableCell>
                                    <TableCell>Date Updated</TableCell>
                                    <TableCell>Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {documentsList && documentsList.filter((item: { uploaded_by: number; }) => item.uploaded_by !== authData?.user?.id).length > 0 ? documentsList.filter((item: { uploaded_by: number; }) => item.uploaded_by !== authData?.user?.id).map((document: { date_updated: string; id: React.Key; name: string; url: string | URL; }) => (
                                    <TableRow key={document.id}>
                                        <TableCell>{document.name}</TableCell>
                                        <TableCell>{dayjs(document.date_updated).format('MM/DD/YY')}</TableCell>
                                        <TableCell>
                                            <Button onClick={() => {
                                                window.open(document.url, '_blank');
                                            }} size="small" variant="contained" color="primary">
                                                View
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                )) : isLoadingDocumentsList ? (
                                    <TableRow>
                                        <TableCell colSpan={3} align="center">
                                            <Box sx={{
                                                display: 'flex', alignItems: "center", justifyContent: "center",
                                                textAlign: "center", alignSelf: "center",
                                                width: 'auto', height: '100px', margin: "auto"
                                            }}>

                                                <CircularProgress />
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    <TableRow>
                                        <TableCell colSpan={3} align="center">No Received Documents</TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Stack>
            </Stack >
            {showShareDocumentModal && (
                <ShareDocumentModal
                    handleClose={() => setShowShareDocumentModal(false)}
                    company_id={companyId}
                />
            )
            }
        </>
    );
};

export default RockerboxDocuments;
